'use client'

import { useEffect, useState } from "react"
import Dropdown from "@/components/form/Dropdown"
import moment from "moment"
import { formatPrice } from "@/services/number"
import { getTotalPrice } from "@/services/booking"
import { createObjectActionPagePathname, createObjectPagePathname } from "@/services/pathname"
import { getNextSessions, sortDepartures } from "@/services/holidayStay"
import { useSearchParams, useRouter } from "next/navigation"
import Contact from "./Contact"

type BookingSidebarProps = {
    holidayStay: HolidayStay,
    session: HolidayStaySession
}

export default function BookingSidebar({
    holidayStay,
    session
}: BookingSidebarProps) {

    const searchParams = useSearchParams()
    const { replace } = useRouter()

    // TODO : adapter selon searchParams
    const [booking, setBooking] = useState({
        session,
        departure: sortDepartures(session.departures)[0],
        options: []
    })

    useEffect(() => {
        setBooking({
            ...booking,
            session,
            departure: session.departures.filter(departure => departure.city === booking.departure.city)[0] ?? session.departures[0]

        })
    }, [session])

    if (booking === null) {
        return <></>
    }

    return (
        <>
            <div className="d-flex flex-column flex-md-row flex-lg-column justify-end" data-offset={450}>
                <div className="encart-1 ml-lg-0 px-30 py-30 bg-bloc w-360">
                    <div className="row y-gap-15 items-center justify-between">
                        <div className="col-auto">
                            <div className="text-14 text-light-1">
                                {/*<h4>A partir de {Math.floor(Math.min(...holidayStay.sessions[0].departures.map(departure => departure.price)))/100}€ par enfant</h4>*/}
                                <h4>A partir de {Math.floor(Math.min(...getNextSessions(holidayStay).map(session => Math.min(...session.departures.map(departure => departure.price)))) / 100)}€ par enfant</h4>
                                <span className="info">hors assurance</span>
                            </div>
                        </div>
                    </div>
                    <div className="row y-gap-20 pt-5">
                        <div className="col-12">
                            <Dropdown
                                className="mb-15"
                                withLabel={true}
                                options={getNextSessions(holidayStay).sort((a, b) => {
                                    return a.startDate < b.startDate ? -1 : 1
                                }).reduce((options: any, session) => {
                                    return {
                                        ...options,
                                        [session.id]: {
                                            label: moment(session.startDate).format('DD/MM/YYYY') + ' au ' + moment(session.endDate).format('DD/MM/YYYY'),
                                            sub: session.availablePlaces > 0 ? 'à partir de ' + formatPrice(Math.min(...session.departures.map(departure => departure.price))) + '€' : 'COMPLET'
                                        }
                                    }
                                }, {})}
                                subTitle={(moment(booking.session.endDate).diff(moment(booking.session.startDate), 'days') + 1) + " jours, " + moment(booking.session.endDate).diff(moment(booking.session.startDate), 'days') + " nuits"}
                                value={session.id}
                                nullable={false}
                                multiple={false}
                                search={false}
                                onChange={(value) => {
                                    /*setBooking({
                                        ...booking,
                                        session: holidayStay.sessions.filter(s => s['@id'] === value)[0] ?? holidayStay.sessions[0]
                                    })*/
                                    replace(createObjectPagePathname('HolidayStay', holidayStay, { session: value }), { scroll: false })
                                }}
                            />
                            {session.availablePlaces > 0 ?
                                <>
                                    <Dropdown
                                        className="mb-15"
                                        withLabel={true}
                                        options={sortDepartures(session.departures).reduce((options: any, departure: any) => {
                                            return {
                                                ...options,
                                                [departure.id]: {
                                                    label: departure.city,
                                                    sub: formatPrice(departure.price) + '€'
                                                }
                                            }
                                        }, {})}
                                        subTitle="Ville de départ"
                                        value={booking.departure.id}
                                        nullable={false}
                                        multiple={false}
                                        search={false}
                                        onChange={(value) => {
                                            setBooking({
                                                ...booking,
                                                departure: session.departures.filter(d => d.id === value)[0] ?? session.departures[0]
                                            })
                                        }}
                                    />
                                    {session.options.length > 0 &&
                                        <Dropdown
                                            className="mb-15"
                                            withLabel={true}
                                            options={session.options.reduce((options: any, option: any) => {
                                                return {
                                                    ...options,
                                                    [option.id]: {
                                                        label: option.name,
                                                        sub: formatPrice(option.price) + '€'
                                                    }
                                                }
                                            }, {})}
                                            subTitle="Options"
                                            value={booking.options.map(option => option.id)}
                                            placeholder="Aucune option"
                                            nullable={false}
                                            multiple={{
                                                maxDisplayedItems: 2,
                                                overMaxDisplayedItemsLabel: " options"
                                            }}
                                            search={false}
                                            onChange={(value) => {
                                                setBooking({
                                                    ...booking,
                                                    options: value.map(o => session.options.filter(option => option.id === o)[0] ?? null).filter(option => option)
                                                })
                                            }}
                                        />
                                    }
                                    <div className="col-auto">
                                        <div className="d-flex justify-content-end">
                                            <div className="text-14 text-right mr-10">
                                                <div className="total">Total : {formatPrice(getTotalPrice('HolidayStay', booking))}€</div>
                                                <div className="info text-13 text-light-1">
                                                    hors assurance
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 mt-15">
                                        <a href={createObjectActionPagePathname('HolidayStay', holidayStay, 'booking', {
                                            session: session.id,
                                            departure: booking.departure.id,
                                            options: booking.options.map(option => option['@id']).join(',')
                                        })} className="button-primary d-block w-100">
                                            Réserver
                                        </a>
                                        {session.availablePlaces < 5 &&
                                            <p className="last-chance text-13 font-primary">
                                                Dernière{session.availablePlaces > 1 && <>s</>} place{session.availablePlaces > 1 && <>s</>} disponible{session.availablePlaces > 1 && <>s</>}
                                            </p>
                                        }                                        
                                    </div>
                                </>
                                :
                                <>
                                    <div className="badge rounded-pill bg-terracotta fs-5 w-100 font-primary">COMPLET</div>
                                </>
                            }
                        </div>
                    </div>
                    {/* TODO : à activer plus tard */}
                    {/*<span className="info d-block mt-10">
                        <i className="fi fi-rr-info" />
                        Vous pourrez ajouter des participants à l'étape suivante.
                    </span>*/}
                </div>
                <Contact
                    holidayStay={holidayStay}
                    session={session}
                />
            </div>
        </>
    )
}